/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "outreach",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Syncing sequence states",
          "depth": 1,
          "id": "syncing-sequence-states"
        }, {
          "value": "Sync performance and parallelization",
          "depth": 0,
          "id": "sync-performance-and-parallelization"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      strong: "strong",
      em: "em",
      img: "img"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "After selecting Outreach from our Destinations catalog, you will be prompted to\ninitiate an OAuth flow. You will then be redirected back to Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Outreach objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Accounts"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Prospects"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Sequence States"
          }), " (instance of a prospect within a sequence)"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Here are the possible modes for how to alter Outreach objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new objects to Outreach and updates fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing objects in Outreach. It doesn't add new objects."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "Sequence States"
        }), " will only have ", _jsx(_components.em, {
          children: "insert"
        }), " mode"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": pushes new objects to Outreach. It doesn't update any objects."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Outreach workspace by the given fields in the dropdown."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-outreach-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "The record should be matched using a unique identifier, otherwise the intended\nrecords may not be updated properly."
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Outreach's object properties, including custom fields, and relationships.\nYou will still need to refer to the custom field number, despite having a different custom display name."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-outreach-field-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-outreach-custom-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-sequence-states",
        children: "Syncing sequence states"
      }), "\n", _jsx(_components.p, {
        children: "Syncing sequence state objects is adding prospects to their corresponding sequences. To successfully start a sequence and engage\nwith the prospect, you must make sure your sequence has the correct settings and sending and syncing messages from mailbox are enabled (if emailing)."
      }), "\n", _jsx(_components.p, {
        children: "Creating a sequence state requires that you have information on all of these Outreach objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "prospects"
        }), "\n", _jsx(_components.li, {
          children: "sequences"
        }), "\n", _jsx(_components.li, {
          children: "mailboxes"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Here is an example on how to set up the configurations:\n", _jsx(_components.img, {
          src: "destinations/destination-outreach-sequence-state.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.p, {
        children: "You will select a field from your model to match to the corresponding object. You need to make sure you are using a unique identifier.\nHightouch will use that to look up each of the corresponding object's ID and combine them to create a sequence state."
      }), "\n", _jsx(_components.h2, {
        id: "sync-performance-and-parallelization",
        children: "Sync performance and parallelization"
      }), "\n", _jsx(_components.p, {
        children: "Outreach has a fixed limit of 10,000 requests per hour per user. This limit resets every hour on the dot.\nTo accommodate this limit and avoid potentially impacting your usage of the Outreach desktop app during the sync run, Hightouch will draw out the sync by sending just one request at a time.\nIf the limit is hit during a sync run, Hightouch will wait until the next hour to continue the rest of the sync.\nNote that the wait time can vary depending on when the rate limit is hit."
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to speed up your sync, you may increase the parallelization to send more requests in parallel.\n", _jsx(_components.img, {
          src: "destinations/destination-outreach-parallelization.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.p, {
        children: "Keep in mind that this will increase the risk of hitting the rate limit. We recommend that you run a test through the \"Test a row\"\nfeature in the sync configuration form page and spot check how many requests are made. You can then estimate how many requests\neach sync run will make based on how many rows you expect to be added, changed, or removed. If you estimate that you will exceed 10,000 requests,\nit's best to not increase the parallelization. Another safety net is to create a Hightouch specific user\nfor the sync so it doesn't lock out other users from using the Outreach app."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
